import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getWishlistShare } from "@/lib/wishlist.functions";
import { resolveImage } from "@/lib/image-map";
import { formatPrice, SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { CopyButton } from "@/components/copy-button";

const shareQuery = (token: string, fn: typeof getWishlistShare) =>
  queryOptions({
    queryKey: ["wishlist-share", token],
    queryFn: () => fn({ data: { token } }),
    staleTime: 1000 * 60 * 5,
  });

export const Route = createFileRoute("/wishlist/share/$token")({
  head: () => ({
    meta: [
      { title: "Shared wishlist — Pluggz" },
      { name: "description", content: "A wishlist of pieces shared from Pluggz creator storefronts." },
      { property: "og:title", content: "Shared wishlist — Pluggz" },
      { property: "og:description", content: "A wishlist of pieces shared from Pluggz creator storefronts." },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(
      shareQuery(params.token, getWishlistShare as any),
    );
    if (!data?.share) throw notFound();
  },
  component: SharePage,
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="text-sm text-muted-foreground">Something went wrong loading this wishlist.</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Wishlist not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">This share link is invalid or has been removed.</p>
      <Link to="/discover" className="mt-8 inline-block border-b border-foreground pb-1 text-sm">Discover on Pluggz</Link>
    </div>
  ),
});

function SharePage() {
  const { token } = Route.useParams();
  const fn = useServerFn(getWishlistShare);
  const { data } = useSuspenseQuery(shareQuery(token, fn as any));
  const products = data?.products ?? [];

  return (
    <div className="mx-auto max-w-7xl px-6 pt-12 pb-24">
      <Reveal>
        <p className="editorial-eyebrow">Shared with you</p>
        <h1 className="mt-2 font-serif text-5xl">
          <span className="italic shimmer">A wishlist</span>
        </h1>
        <p className="mt-3 text-muted-foreground">
          {products.length} {products.length === 1 ? "piece" : "pieces"} curated on Pluggz.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <CopyButton
            content={typeof window !== "undefined" ? window.location.href : ""}
            size="sm"
            variant="outline"
            className="h-8 px-3 text-xs uppercase tracking-widest"
          >
            Copy link
          </CopyButton>
        </div>
      </Reveal>

      {products.length === 0 ? (
        <div className="mt-16 border border-border bg-card/40 px-6 py-12 text-center">
          <p className="text-sm text-muted-foreground">This wishlist is empty.</p>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
          {products.map((p: any, i: number) => (
            <Reveal key={p.id} delay={(i % 4) * 90}>
              <Link
                to="/product/$id"
                params={{ id: p.id }}
                className="group block border border-border bg-card overflow-hidden rounded-sm"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                  <img
                    src={resolveImage(p.image_key)}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 space-y-1">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{p.brand}</p>
                  <p className="text-sm">{p.title}</p>
                  <p className="text-sm text-muted-foreground">{formatPrice(p.price, p.currency)}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}

      <div className="mt-16 flex justify-center">
        <Link
          to="/discover"
          className="inline-flex items-center border border-foreground/80 px-6 py-3 text-xs uppercase tracking-widest text-foreground transition-colors hover:bg-foreground hover:text-background"
        >
          Shop on Pluggz
        </Link>
      </div>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">{SITE_URL.replace(/^https?:\/\//, "")}</Link>
      </p>
    </div>
  );
}
